import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { getErrorMessage } from '@/lib/api'
import { WorkerProfileLink } from '@/pages/attendance/worker-link'
import { formatClock, formatDay } from '@/pages/attendance/data'
import { useTenantTimeZone } from '@/lib/tenant-date-time'
import { workersApi, type WorkerAttendanceRequest } from '@/services/workers'

const REQUEST_LABEL: Record<WorkerAttendanceRequest['request_type'], string> = {
  missing_punch: 'Missing punch',
  time_correction: 'Time correction',
}

export default function AttendanceApprovalsPage() {
  const queryClient = useQueryClient()
  const attendanceTimeZone = useTenantTimeZone()
  const requestsQuery = useQuery({
    queryKey: ['attendance-requests', 'pending'],
    queryFn: () => workersApi.getAdminAttendanceRequests({ status: 'pending' }),
  })
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['attendance-requests'] })
    await queryClient.invalidateQueries({ queryKey: ['attendance-punches'] })
    await queryClient.invalidateQueries({ queryKey: ['worker-attendance'] })
    await queryClient.invalidateQueries({ queryKey: ['worker-attendance-latest'] })
  }
  const approve = useMutation({
    mutationFn: (id: string | number) => workersApi.approveAdminAttendanceRequest(id),
    onSuccess: async () => {
      toast.success('Request approved. The time log was updated.')
      await refresh()
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
  const reject = useMutation({
    mutationFn: (id: string | number) => workersApi.rejectAdminAttendanceRequest(id),
    onSuccess: async () => {
      toast.success('Request rejected')
      await refresh()
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
  const requests = requestsQuery.data ?? []

  return (
    <div className="space-y-5 pb-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Approvals</h1>
        <p className="mt-1 text-sm text-slate-500">Review missing punch and time correction requests raised from time logs.</p>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <Stat label="Pending" value={requests.length} />
        <Stat label="Missing punch" value={requests.filter((request) => request.request_type === 'missing_punch').length} />
        <Stat label="Time correction" value={requests.filter((request) => request.request_type === 'time_correction').length} />
      </div>

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-900">Pending requests</div>
        {requestsQuery.isLoading ? (
          <div className="space-y-2 p-4">{Array.from({ length: 3 }).map((_, index) => <Skeleton key={index} className="h-12 w-full" />)}</div>
        ) : requestsQuery.error ? (
          <p className="p-6 text-sm text-destructive">{getErrorMessage(requestsQuery.error)}</p>
        ) : requests.length === 0 ? (
          <p className="p-8 text-center text-sm text-slate-500">No pending missing punch or time correction requests.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  {['Employee', 'Request', 'Date', 'Check in', 'Check out', 'Reason', ''].map((heading) => (
                    <th key={heading} className="px-4 py-3 font-medium">{heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {requests.map((request) => (
                  <tr key={request.id} className="border-t border-slate-100">
                    <td className="px-4 py-3">
                      <WorkerProfileLink workerId={request.worker} name={request.worker_name || 'Worker'} className="font-medium text-slate-900" />
                    </td>
                    <td className="px-4 py-3">{REQUEST_LABEL[request.request_type]}</td>
                    <td className="px-4 py-3">{formatRequestDate(request.work_date, attendanceTimeZone)}</td>
                    <td className="px-4 py-3 tabular-nums">{formatClock(request.requested_check_in, attendanceTimeZone)}</td>
                    <td className="px-4 py-3 tabular-nums">{formatClock(request.requested_check_out, attendanceTimeZone)}</td>
                    <td className="px-4 py-3 text-slate-600">{request.reason || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <Button type="button" size="sm" variant="outline" disabled={reject.isPending} onClick={() => reject.mutate(request.id)}>Reject</Button>
                        <Button type="button" size="sm" disabled={approve.isPending} onClick={() => approve.mutate(request.id)}>Approve</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}

function formatRequestDate(value: string, timeZone?: string | null) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return formatDay(value, timeZone)
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])).toLocaleDateString([], {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-2xl font-semibold text-slate-900">{value}</div>
    </div>
  )
}
